import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
// import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActionAreaProps,
  Container,
  Stack,
  StackProps,
  Tooltip,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import Joyride, { Placement } from 'react-joyride';
import { TP_COLORS } from '../../../constants';
import {
  isMobileScreen,
  getUrlConvertedString,
  getSubjectIconName,
  getSubjectColorNew,
  checkSubjectCodeHide,
  getReleaseNoteShowedInSubjectPageFromLocalStorage,
  setReleaseNoteShowedInSubjectPageToLocalStorage,
} from '../../../helper';
import { useAppSelector } from '../../../hooks';

import { getAuthState } from '../../../store/AuthSlice';
import {
  ICurriculumForHome,
  ISubjectForHome,
  getCurriculumState,
  getCurriculumsForHomePage,
} from '../../../store/CurriculumSlice';
import {
  getTopicState,
  getTopicsWithSubTopics,
} from '../../../store/TopicSlice';
import { IGroupedTopic } from '../../../store/SubTopicSlice';
import {
  getProgressState,
  getSubjectProgress,
} from '../../../store/ProgressSlice';

import SubjectProgressBar from './components/SubjectProgressBar';
import CircularProgressWithValue from './components/CircularProgressWithValue';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import AssessmentTab from './components/AssessmentTab';
import HomeFooter from '../../Home/components/HomeFooter';
import FullScreenLoader from '../../../components/Loaders/FullScreenloader';
import SubTopicList from './components/SubTopicList';
import NotFound from '../../Validate/NotFound';
import { getTopicUrl } from '../Topic/components/SubTopicSection';
import { getTenantsState } from '../../../store/V2/TenantsSlice';

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const subjectNameTypoProps: TypographyProps['sx'] = {
  component: 'h1',
  variant: 'h5',
  fontSize: '30px',
  marginRight: '20px',
};

const subjectDescriptionTypoProps: TypographyProps = {
  fontSize: '14px',
  fontWeight: '550',
  marginTop: '12px',
};

const subjectCodeTypoProps: TypographyProps = {
  fontSize: '18px',
  fontWeight: '550',
  marginTop: '5px',
};

const cardActionAreaStyles: CardActionAreaProps['sx'] = {
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '20px',
};

const topicNameTypoProps: TypographyProps = {
  component: 'h3',
  variant: 'h6',
  fontSize: '18px',
  color: TP_COLORS.gray.dark,
};

const assessmentStackProps: StackProps['sx'] = {
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'row',
  sx: {
    minHeight: { xs: '60px', sm: '90px' },
    background: `url('${process.env.PUBLIC_URL}/images/subjectPage/shape-half-circle-1.png') top left/contain no-repeat, url('${process.env.PUBLIC_URL}/images/subjectPage/shape-half-circle-2.png') top right/contain no-repeat, #E5F7F6`,
  },
};

export const ellipsisTwoLineTypoStyles: TypographyProps['sx'] = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  color: TP_COLORS.gray.light,
};

const pastPaperCurriculums = [
  'Cambridge IGCSE',
  'Edexcel IGCSE',
  'Edexcel GCSE',
  'AQA GCSE',
  'Edexcel A Levels',
  'Edexcel International A Levels',
  'Cambridge International A Levels',
];

const constructBreadcrumbOptions = (state: IState) => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
  ];

  if (state.selectedCurriculum) {
    const curriculum = getUrlConvertedString(state.selectedCurriculum.name);
    options.push({
      type: 'link',
      title: state.selectedCurriculum.name,
      link: `${process.env.PUBLIC_URL}/${curriculum}`,
    });
  }

  if (state.selectedSubject) {
    options.push({
      type: 'text',
      title: state.selectedSubject.name,
      highlight: true,
    });
  }

  return options;
};

interface IState {
  selectedCurriculum: ICurriculumForHome | undefined;
  selectedSubject: ISubjectForHome | undefined;
}

const initialState: IState = {
  selectedCurriculum: undefined,
  selectedSubject: undefined,
};

const SubjectPage: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const isMobile = isMobileScreen();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Store
  const authState = useAppSelector(getAuthState);
  const topicState = useAppSelector(getTopicState);
  const progressState = useAppSelector(getProgressState);
  const curriculumState = useAppSelector(getCurriculumState);
  const tenantState = useAppSelector(getTenantsState);

  const { appTenant } = tenantState;
  const { curriculumsForHomePage } = curriculumState;
  const { topicsWithSubTopics, status: topicStatus } = topicState;
  const { subjectProgress } = progressState;

  // State
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [state, setState] = useState<IState>(initialState);
  const [isExpand, setIsExpand] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<IGroupedTopic>();
  const [topicList, setTopicList] = useState<Array<IGroupedTopic>>([]);
  const [assessmentList, setAssessmentList] = useState<Array<IGroupedTopic>>(
    []
  );
  const [isJoyrideActive, setIsJoyrideActive] = useState(false);

  const tenantName = appTenant?.name || '';
  const userRole = authState.user?.role.name;
  const pastPaperVersion = 1;
  const releaseNoteShowed = getReleaseNoteShowedInSubjectPageFromLocalStorage();

  const searchParams = new URLSearchParams(location.search);
  const selectedTabIndexFromRoute =
    searchParams.get('assessment-tab') || undefined;

  const subjectColor = getSubjectColorNew(state.selectedSubject?.name || '');
  const breadcrumbOptions = constructBreadcrumbOptions(state);

  useEffect(() => {
    const checkTargetElementLoaded = setInterval(() => {
      const targetElement = document.querySelector('.pastPaperSection');
      if (
        targetElement &&
        (!releaseNoteShowed || parseFloat(releaseNoteShowed) < pastPaperVersion)
      ) {
        setIsJoyrideActive(true);
        clearInterval(checkTargetElementLoaded);
      }
    }, 100);

    return () => clearInterval(checkTargetElementLoaded);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (curriculumsForHomePage.length === 0) {
      dispatch(getCurriculumsForHomePage());
    }

    if (isMobile) {
      setIsExpand(false);
    }
  }, []);

  useEffect(() => {
    if (assessmentList.length > 0 && selectedTabIndexFromRoute) {
      const scrollToAssessment = () => {
        const assessmentSection = document.getElementById('assessment-section');
        if (assessmentSection) {
          const offset = 120;
          const elementPosition =
            assessmentSection.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      };

      const timeoutId = setTimeout(scrollToAssessment, 1000);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [assessmentList, selectedTabIndexFromRoute]);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      const selectedCurriculum = curriculumsForHomePage.find(
        (curriculumItem) =>
          getUrlConvertedString(curriculumItem.name) === params.curriculum
      );

      if (selectedCurriculum && selectedCurriculum.subjects.length) {
        const selectedSubject = selectedCurriculum.subjects.find(
          (subjectItem) =>
            getUrlConvertedString(subjectItem.name) === params.subject &&
            getUrlConvertedString(subjectItem.subjectLevel) ===
              params.subjectLevel &&
            getUrlConvertedString(subjectItem.subjectCode) ===
              params.subjectCode
        );

        if (selectedSubject) {
          setState({
            selectedCurriculum,
            selectedSubject,
          });
        }
      }
      setInitialLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (
      topicStatus !== 'loading' &&
      state.selectedCurriculum &&
      state.selectedSubject &&
      checkTopicsFetched()
    ) {
      dispatch(
        getTopicsWithSubTopics({
          curriculum: state.selectedCurriculum.name,
          subject: state.selectedSubject.name,
          subjectCode: state.selectedSubject.subjectCode,
          subjectLevel: state.selectedSubject.subjectLevel,
        })
      );
    }

    if (
      userRole === 'Student' &&
      state.selectedCurriculum &&
      state.selectedSubject
    ) {
      dispatch(
        getSubjectProgress({
          curriculum: state.selectedCurriculum.name,
          subject: state.selectedSubject.name,
          subjectLevel: state.selectedSubject.subjectLevel,
        })
      );
    }
  }, [state.selectedSubject]);

  useEffect(() => {
    if (topicsWithSubTopics.length) {
      const topics = topicsWithSubTopics.filter(
        (item) => !item.isAssessment && item.name !== 'Overview'
      );

      if (topics.length) {
        setTopicList(topics);
        if (!isMobile) {
          setSelectedTopic(topics[0]);
        }
      }

      setAssessmentList(
        topicsWithSubTopics.filter((item) => item.isAssessment)
      );
    }
  }, [topicsWithSubTopics]);

  const checkTopicsFetched = () => {
    if (
      state.selectedSubject &&
      topicsWithSubTopics.length &&
      topicsWithSubTopics[0].subject === state.selectedSubject.name &&
      topicsWithSubTopics[0].subjectLevel ===
        state.selectedSubject.subjectLevel &&
      topicsWithSubTopics[0].subjectCode === state.selectedSubject.subjectCode
    ) {
      return false;
    }
    return true;
  };

  const getTopicProgress = (topicData: IGroupedTopic) => {
    let progressValue = 0;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicData._id
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const { subTopics } = topicProgress;

        const totalProgress = subTopics.reduce((accumulator, object) => {
          return accumulator + object.completion;
        }, 0);

        progressValue = totalProgress / (topicData.subTopics.length || 1);
      }
    }
    return progressValue;
  };

  const getTopicDescription = (topic: IGroupedTopic) => {
    const filteredSubTopics: string[] = [];

    if (topic.subTopics.length) {
      topic.subTopics.forEach((subTopic) => {
        if (
          subTopic.name.toLocaleLowerCase().search('worksheet') < 0 &&
          subTopic.name.toLocaleLowerCase().search('overview') < 0 &&
          subTopic.name.toLocaleLowerCase().search('topic planner')
        ) {
          filteredSubTopics.push(subTopic.name);
        }
      });
    }

    return `The ${
      topic.name
    } topic covers fundamental concepts such as ${filteredSubTopics
      .map((subTopicName) => subTopicName)
      .join(', ')}.`;
  };

  const getOverviewUrl = (): string => {
    const overviewTopic = topicsWithSubTopics.find(
      (item) => item.name.toLowerCase() === 'overview'
    );

    return overviewTopic ? getTopicUrl(overviewTopic, params) : '';
  };

  const isOverviewAvailable = () =>
    !topicsWithSubTopics.find((item) => item.name === 'Overview');

  // let metaTitle = `IGCSE And GCSE - Maths And Science Questions with Answers By Topic | ${tenantName}`;

  // let metaDesc =
  //   'Science and Maths questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!';

  // if (state.selectedCurriculum) {
  //   metaTitle = `${
  //     state.selectedCurriculum.name || ''
  //   } - Maths And Science Questions with Answers By Topic | ${tenantName}`;

  //   metaDesc = `${
  //     state.selectedCurriculum.name || ''
  //   } maths and science questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!`;
  // }

  //   metaDesc = `${
  //     state.selectedCurriculum.name || ''
  //   } maths and science questions with answers organized by topic. Our questions and answers are designed to help students prepare for their exams. Give them a try today!`;
  // }

  if (initialLoading || topicStatus === 'loading') {
    return <FullScreenLoader />;
  }

  if (!initialLoading && !state.selectedSubject) {
    return <NotFound />;
  }

  const getPastPapersData = () => {
    const curriculumName = getUrlConvertedString(
      state.selectedCurriculum?.name || ''
    );
    const subjectName = getUrlConvertedString(
      state.selectedSubject?.name || ''
    );
    const subjectLevel = getUrlConvertedString(
      state.selectedSubject?.subjectLevel || ''
    );
    const subjectCode = getUrlConvertedString(
      state.selectedSubject?.subjectCode || ''
    );

    return {
      url: `${process.env.PUBLIC_URL}/dashboard/${curriculumName}/${subjectName}/${subjectLevel}/${subjectCode}/past-papers`,
    };
  };

  const steps = [
    {
      target: '.pastPaperSection',
      content: (
        <>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '1.05rem',
              textAlign: 'left',
              mt: '-10px',
            }}
          >
            Explore Past Papers
          </Typography>
          <br />
          <video
            src={`${process.env.PUBLIC_URL}/images/pastPaperPage/releaseNote/newrelease.mov`}
            style={{
              width: '115%',
              height: 'auto',
              marginLeft: '-25px',
            }}
            autoPlay
            loop
            muted
            playsInline
          />
          <Typography
            sx={{ fontSize: '13px', textAlign: 'left', mt: 2, mb: 2 }}
          >
            Need more practice? Access a collection of past exam papers to test
            your skills and prepare for upcoming exams. Just click the Past
            Papers button to get started!
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: '-10px',
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#1E293B',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#787878',
                },
              }}
              onClick={() => {
                setReleaseNoteShowedInSubjectPageToLocalStorage(
                  pastPaperVersion.toString()
                );
                setIsJoyrideActive(false);
              }}
            >
              Dismiss
            </Button>
          </Box>
        </>
      ),
      disableBeacon: true,
      placement: 'left-start' as Placement,
      hideFooter: true,
      disableScrolling: true,
    },
  ];

  const PastPapersSection = () => (
    <Box
      display="flex"
      alignItems="center"
      sx={userRole === 'Student' ? { marginRight: '20px' } : null}
      className="pastPaperSection"
    >
      <Tooltip
        title="Explore Past Paper Today"
        placement="top"
        arrow
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -14],
                },
              },
            ],
          },
        }}
      >
        <Button
          component={Link}
          variant="text"
          startIcon={
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              bgcolor={`${subjectColor}1A`}
              borderRadius="50%"
              width={35}
              height={35}
            >
              <DescriptionIcon
                style={{ color: subjectColor, fontSize: '20px' }}
              />
            </Box>
          }
          style={{ textTransform: 'none', color: subjectColor }}
          disableRipple
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
              color: subjectColor,
            },
          }}
          to={getPastPapersData().url}
          // state={getPastPapersData()}
        >
          {!isMobile && 'Past Papers'}
          {!isMobile && (
            <Box
              padding="1px 7px 1px 7px"
              ml="8px"
              bgcolor="#1E293B"
              color={TP_COLORS.white}
              borderRadius={1}
            >
              <Typography sx={{ fontSize: '12px' }}>New</Typography>
            </Box>
          )}
        </Button>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      {/* <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet> */}
      <Joyride hideCloseButton steps={steps} run={isJoyrideActive} />

      <div style={{ minHeight: '80vh' }}>
        <Container maxWidth="lg" sx={{ mb: '60px', pt: '10px' }}>
          <Stack {...topStackProps}>
            <Breadcrumbs
              options={breadcrumbOptions}
              showDropDown={belowMd}
              textColor={TP_COLORS.gray.light}
              highlightColor={subjectColor}
            />

            <Box display="flex" alignItems="center" justifyContent="flex-end">
              {pastPaperCurriculums.includes(
                state.selectedCurriculum?.name || ''
              ) && <PastPapersSection />}
              {userRole === 'Student' && <SubjectProgressBar />}
            </Box>
          </Stack>
          <Box
            component="section"
            display="flex"
            sx={{
              color: TP_COLORS.white,
              borderRadius: '7px',
              background: `url('${process.env.PUBLIC_URL}/images/subjectPage/shape-header.png') top right/contain no-repeat, ${subjectColor}`,
            }}
          >
            <Box padding="24px">
              <Typography {...subjectNameTypoProps}>
                {state.selectedCurriculum?.name || ' '}{' '}
                {state.selectedSubject?.name || ''} Revision Resources
              </Typography>
              {!checkSubjectCodeHide(state.selectedCurriculum?.name || '') && (
                <Typography {...subjectCodeTypoProps}>
                  Course code: {state.selectedSubject?.subjectCode || ''}
                </Typography>
              )}

              <Typography {...subjectDescriptionTypoProps}>
                Our <strong>{state.selectedCurriculum?.name || ' '} </strong>
                <strong>{state.selectedSubject?.name || ''}</strong> course{' '}
                <strong>({state.selectedSubject?.subjectCode || ''})</strong> is
                tailored to meet the latest{' '}
                <strong>{state.selectedCurriculum?.name || ' '}</strong>{' '}
                standards. Our comprehensive module includes video lessons,
                interactive quizzes, worked examples, and downloadable
                worksheets. You&apos;ll also find exam-style questions, past
                paper solutions, and personalized AI feedback to track your
                progress. This course is ideal for for self-study,
                homeschooling, or school revision support.
              </Typography>

              <Button
                component={Link}
                color="inherit"
                variant="contained"
                sx={{
                  color: subjectColor,
                  backgroundColor: TP_COLORS.white,
                  fontWeight: '600',
                  marginTop: '12px',
                  '&:disabled': {
                    opacity: 0.5,
                    backgroundColor: TP_COLORS.white,
                    color: subjectColor,
                  },
                }}
                to={getOverviewUrl()}
                disabled={isOverviewAvailable()}
              >
                View Course Overview
              </Button>
            </Box>
            <Box display="flex" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Stack justifyContent="center">
                <img
                  src={`${
                    process.env.PUBLIC_URL
                  }/images/landingPage/icon-${getSubjectIconName(
                    state.selectedSubject?.name || ''
                  )}-white.avif`}
                  alt="subject-img"
                  style={{ height: '150px', margin: '5px' }}
                />
              </Stack>
            </Box>
          </Box>

          <Box marginTop="30px" display="flex">
            <Stack
              component="section"
              sx={{
                width: { xs: '100%', sm: '50%', md: '60%', lg: '65%' },
              }}
            >
              {topicList.length
                ? topicList
                    .slice(0, isExpand ? topicList.length : 5)
                    .map((topicItem, topicIndex) => {
                      const isSelected = selectedTopic === topicItem;
                      return (
                        <Card
                          key={topicIndex}
                          elevation={0}
                          sx={{
                            backgroundColor: isSelected
                              ? `${subjectColor}1A`
                              : TP_COLORS.paperGray.light,
                            borderRadius: '10px',
                          }}
                        >
                          <CardActionArea
                            component={Link}
                            to={getTopicUrl(topicItem, params)}
                            sx={cardActionAreaStyles}
                            onMouseOver={() => setSelectedTopic(topicItem)}
                          >
                            <CircularProgressWithValue
                              value={getTopicProgress(topicItem) || 0}
                              progressProps={{ marginRight: '5px' }}
                              color={subjectColor}
                            />
                            <Box marginLeft="20px">
                              <Typography {...topicNameTypoProps}>
                                {topicItem.name}
                              </Typography>
                              <Typography
                                component="p"
                                variant="subtitle2"
                                sx={ellipsisTwoLineTypoStyles}
                              >
                                {getTopicDescription(topicItem)}
                              </Typography>
                            </Box>
                          </CardActionArea>
                        </Card>
                      );
                    })
                : null}
            </Stack>

            {/* SubTopics Section */}
            {selectedTopic && <SubTopicList selectedTopic={selectedTopic} />}
          </Box>

          {/* View more button  */}
          {isMobile && topicList.length > 5 ? (
            <Stack alignItems="center" marginTop="10px">
              <Button
                color="secondary"
                onClick={() => setIsExpand((prevState) => !prevState)}
              >
                View {isExpand ? 'less' : 'more'}
              </Button>
            </Stack>
          ) : null}
        </Container>

        {/* Test Your Skills */}
        {assessmentList.length ? (
          <>
            <Box id="assessment-section">
              <Stack {...assessmentStackProps}>
                <Typography
                  component="h2"
                  variant={isMobile ? 'subtitle1' : 'h5'}
                  fontWeight="600"
                >
                  Test Your Skills
                </Typography>
              </Stack>
              <Container maxWidth="lg" sx={{ mt: '50px', mb: '60px' }}>
                <AssessmentTab
                  assessmentList={assessmentList}
                  subjectProgress={subjectProgress}
                  initialTab={selectedTabIndexFromRoute}
                />
              </Container>
            </Box>
          </>
        ) : null}
      </div>

      <HomeFooter />
    </Box>
  );
};

export default SubjectPage;
